import React, { useState } from "react";
import Modal from "components/common/Modal";
import { Button, TextInput, Dropdown } from "components/common";
import Client from "lib/api/Client";
import {
  PencilIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  PaperAirplaneIcon,
  CheckIcon,
  XMarkIcon,
  KeyIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";

const EMPTY_CLOUD_USER = {
  first_name: "",
  last_name: "",
  email: "",
  user_type: "standard",
  username: "",
  status: "active",
};

export const CLOUD_TABLE_STYLES = {
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    fontSize: 11,
    fontWeight: 600,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    borderBottom: "2px solid #e5e7eb",
    background: "#f9fafb",
    whiteSpace: "nowrap",
  },
  td: { padding: "10px 12px", borderBottom: "1px solid #f3f4f6", color: "#111827", verticalAlign: "middle" },
};

export const CLOUD_ICON_BTN = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  border: "1px solid #d1d5db",
  borderRadius: 6,
  background: "#fff",
  cursor: "pointer",
  padding: 0,
};

const CLOUD_STATUS_COLORS = {
  active: { bg: "#f0fdf4", color: "#15803d" },
  pending: { bg: "#fffbeb", color: "#b45309" },
  inactive: { bg: "#f3f4f6", color: "#6b7280" },
};

const CLOUD_STATUS_PILL = {
  display: "inline-block",
  padding: "2px 10px",
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 500,
  textTransform: "capitalize",
};

export function formatCloudUserDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "numeric",
    day: "numeric",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
}

function escapeCSVField(value) {
  if (value == null) return "";
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

function exportCloudUsersCSV(users, clientName) {
  const headers = ["First Name", "Last Name", "Email", "Username", "User Type", "Status", "Password Set", "Uses SSO", "Credentials Sent", "Created", "Updated"];
  const rows = users.map((u) => [
    u.first_name,
    u.last_name,
    u.email,
    u.username || "",
    u.user_type,
    u.status,
    u.password_set === undefined || u.password_set === null ? "" : u.password_set ? "Yes" : "No",
    u.is_sso === undefined || u.is_sso === null ? "" : u.is_sso ? "Yes" : "No",
    u.credentials_sent_at || "",
    u.created_at || "",
    u.updated_at || "",
  ]);
  const csv = [headers, ...rows].map((row) => row.map(escapeCSVField).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `cloud-users-${(clientName || "export").replace(/[^a-z0-9]/gi, "-").toLowerCase()}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function renderBoolIcon(value, trueLabel, falseLabel) {
  if (value === undefined || value === null) return <span style={{ color: "#9ca3af" }}>—</span>;
  return value ? (
    <CheckIcon title={trueLabel} aria-label={trueLabel} style={{ width: 16, height: 16, color: "#15803d" }} />
  ) : (
    <XMarkIcon title={falseLabel} aria-label={falseLabel} style={{ width: 16, height: 16, color: "#9ca3af" }} />
  );
}

function userLabel(u, withUsername) {
  return `${u.first_name} ${u.last_name} (${withUsername ? u.username : u.email})`;
}

function ConfirmModal({ show, title, children, confirmLabel, confirmClass = "button", onConfirm, onCancel }) {
  return (
    <Modal show={show}>
      <div style={{ padding: "24px", width: "100%" }}>
        <h2 style={{ margin: "0 0 16px 0" }}>{title}</h2>
        <p style={{ marginBottom: "20px" }}>{children}</p>
        <div className="button-group">
          <Button className={confirmClass} onClick={onConfirm}>{confirmLabel}</Button>
          <Button className="button button--plain" onClick={onCancel}>Cancel</Button>
        </div>
      </div>
    </Modal>
  );
}

function CloudUserFields({ value, onChange, showLoginUrl }) {
  const set = (field) => (e) => onChange({ ...value, [field]: e.target.value });
  return (
    <>
      <div className="row">
        <div className="col-xs-12 col-sm-6">
          <TextInput label="First Name" type="text" value={value.first_name} onChange={set("first_name")} />
        </div>
        <div className="col-xs-12 col-sm-6">
          <TextInput label="Last Name" type="text" value={value.last_name} onChange={set("last_name")} />
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12 col-sm-6">
          <TextInput label="Email" type="email" value={value.email} onChange={set("email")} />
        </div>
        <div className="col-xs-12 col-sm-6">
          <TextInput label="Username (optional)" type="text" value={value.username || ""} onChange={set("username")} />
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12 col-sm-6">
          <Dropdown label="User Type" value={value.user_type} onChange={set("user_type")}>
            <option value="standard">Standard</option>
            <option value="admin">Admin</option>
          </Dropdown>
        </div>
        <div className="col-xs-12 col-sm-6">
          <Dropdown label="Status" value={value.status} onChange={set("status")}>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="inactive">Inactive</option>
          </Dropdown>
        </div>
      </div>
      {showLoginUrl && (
        <div className="row">
          <div className="col-xs-12">
            <TextInput label="Login URL (optional)" type="text" value={value.login_url || ""} onChange={set("login_url")} />
          </div>
        </div>
      )}
    </>
  );
}

// Shared Cloud Users manager used by the client detail and cloud migration detail pages.
// The parent owns the user list so it can show counts; everything else lives here.
export default function CloudUsersModal({
  show,
  onClose,
  clientUuid,
  clientName,
  cloudType,
  cloudLoginLimit,
  cloudUsers,
  setCloudUsers,
  loading,
  onReload,
  onCredentialsSent,
}) {
  const [showForm, setShowForm] = useState(false);
  const [newUser, setNewUser] = useState(EMPTY_CLOUD_USER);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmSend, setConfirmSend] = useState(null);
  const [confirmReset, setConfirmReset] = useState(null);
  const [sendingUuid, setSendingUuid] = useState(null);
  const [resettingUuid, setResettingUuid] = useState(null);
  const [sendResult, setSendResult] = useState({});
  const [passwordUser, setPasswordUser] = useState(null);
  const [passwordValue, setPasswordValue] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const usedLogins = cloudUsers.filter((u) => u.status !== "inactive").length;
  const atLimit = cloudLoginLimit != null && usedLogins >= cloudLoginLimit;
  const isComplete = (u) => u.first_name && u.last_name && u.email;

  function close() {
    setShowForm(false);
    setEditingUser(null);
    setNewUser(EMPTY_CLOUD_USER);
    setError("");
    onClose();
  }

  async function saveNew() {
    setSaving(true);
    setError("");
    try {
      const payload = {
        first_name: newUser.first_name,
        last_name: newUser.last_name,
        email: newUser.email,
        user_type: newUser.user_type,
        status: newUser.status,
        ...(newUser.username ? { username: newUser.username } : {}),
      };
      const response = await Client.createCloudUser(clientUuid, payload);
      if (response.status === 201) {
        setCloudUsers([...cloudUsers, response.data]);
        setNewUser(EMPTY_CLOUD_USER);
        setShowForm(false);
      } else {
        setError(response.message || "Failed to create cloud user.");
      }
    } catch (e) {
      setError("An error occurred.");
    }
    setSaving(false);
  }

  async function saveEdit() {
    setSaving(true);
    setError("");
    try {
      const payload = {
        first_name: editingUser.first_name,
        last_name: editingUser.last_name,
        email: editingUser.email,
        user_type: editingUser.user_type,
        status: editingUser.status,
        username: editingUser.username || null,
        login_url: editingUser.login_url || null,
      };
      const response = await Client.updateCloudUser(clientUuid, editingUser.uuid, payload);
      if (response.status === 200) {
        setCloudUsers(cloudUsers.map((u) => (u.uuid === editingUser.uuid ? response.data : u)));
        setEditingUser(null);
      } else {
        setError(response.message || "Failed to update cloud user.");
      }
    } catch (e) {
      setError("An error occurred.");
    }
    setSaving(false);
  }

  async function remove(userUuid) {
    try {
      await Client.deleteCloudUser(clientUuid, userUuid);
      setCloudUsers(cloudUsers.filter((u) => u.uuid !== userUuid));
      setConfirmDelete(null);
    } catch (e) {
      console.error(e);
    }
  }

  function setResult(userUuid, ok, message) {
    setSendResult((prev) => ({ ...prev, [userUuid]: { ok, message } }));
  }

  async function sendCredentials(userUuid) {
    setSendingUuid(userUuid);
    try {
      const response = await Client.sendCloudUserCredentials(clientUuid, userUuid);
      if (response.status === 200) {
        setResult(userUuid, true, response.message);
        if (onCredentialsSent) onCredentialsSent();
        onReload();
      } else {
        setResult(userUuid, false, response.message || "Failed to send credentials.");
      }
    } catch (e) {
      setResult(userUuid, false, "An error occurred.");
    }
    setSendingUuid(null);
  }

  async function resetPassword(userUuid) {
    setResettingUuid(userUuid);
    try {
      const response = await Client.resetCloudUserPassword(clientUuid, userUuid, false);
      if (response.status === 200) {
        setResult(userUuid, true, response.message);
      } else {
        setResult(userUuid, false, response.message || "Failed to reset password.");
      }
    } catch (e) {
      setResult(userUuid, false, "An error occurred.");
    }
    setResettingUuid(null);
  }

  async function savePassword() {
    if (!passwordValue) {
      setPasswordError("Password is required.");
      return;
    }
    setPasswordSaving(true);
    setPasswordError("");
    try {
      const response = await Client.setCloudUserPassword(clientUuid, passwordUser.uuid, passwordValue);
      if (response.status === 200) {
        setCloudUsers(cloudUsers.map((u) => (u.uuid === passwordUser.uuid ? response.data : u)));
        setPasswordUser(null);
        setPasswordValue("");
      } else {
        setPasswordError(response.message || "Failed to set password.");
      }
    } catch (e) {
      setPasswordError("An error occurred.");
    }
    setPasswordSaving(false);
  }

  const mutedCell = { ...CLOUD_TABLE_STYLES.td, whiteSpace: "nowrap", color: "#6b7280" };
  const formBox = { background: "#f7fafc", border: "1px solid #e2e8f0", borderRadius: "4px", padding: "16px" };
  const disabledColor = (disabled, color = "#374151") => (disabled ? "#9ca3af" : color);

  return (
    <>
      <Modal show={show} contentStyle={{ padding: 0, maxWidth: "1300px", width: "100%" }}>
        <div style={{ padding: "12px", width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "0 0 16px 0" }}>
            <h2 style={{ margin: 0 }}>Cloud Users</h2>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {cloudLoginLimit != null && (
                <span style={{ fontSize: 14, fontWeight: 600, color: "#6b7280" }}>
                  {usedLogins} / {cloudLoginLimit} cloud licenses used
                </span>
              )}
              {cloudUsers.length > 0 && (
                <button type="button" title="Export CSV" style={CLOUD_ICON_BTN}
                  onClick={() => exportCloudUsersCSV(cloudUsers, clientName)}>
                  <ArrowDownTrayIcon style={{ width: 18, height: 18, color: "#374151" }} />
                </button>
              )}
            </div>
          </div>
          {loading && <p>Loading...</p>}
          {!loading && cloudUsers.length > 0 && (
            <table style={CLOUD_TABLE_STYLES.table}>
              <thead>
                <tr>
                  <th style={CLOUD_TABLE_STYLES.th}>User</th>
                  <th style={CLOUD_TABLE_STYLES.th}>Username</th>
                  <th style={CLOUD_TABLE_STYLES.th}>Type</th>
                  <th style={CLOUD_TABLE_STYLES.th}>Status</th>
                  <th style={{ ...CLOUD_TABLE_STYLES.th, textAlign: "center" }}>Password Set</th>
                  <th style={{ ...CLOUD_TABLE_STYLES.th, textAlign: "center" }}>Uses SSO</th>
                  <th style={CLOUD_TABLE_STYLES.th}>Credentials Sent</th>
                  <th style={CLOUD_TABLE_STYLES.th}>Created</th>
                  <th style={CLOUD_TABLE_STYLES.th}>Updated</th>
                  <th style={{ ...CLOUD_TABLE_STYLES.th, textAlign: "right" }}></th>
                </tr>
              </thead>
              <tbody>
                {cloudUsers.map((u) =>
                  editingUser && editingUser.uuid === u.uuid ? (
                    <tr key={u.uuid}>
                      <td colSpan={10}>
                        <div style={formBox}>
                          <h3 style={{ marginTop: 0 }}>Edit Cloud User</h3>
                          <CloudUserFields value={editingUser} onChange={setEditingUser} showLoginUrl />
                          {error && <p style={{ color: "#e53e3e" }}>{error}</p>}
                          <div className="button-group" style={{ marginTop: "12px" }}>
                            <Button className="button" onClick={saveEdit} disabled={saving || !isComplete(editingUser)}>
                              {saving ? "Saving..." : "Save"}
                            </Button>
                            <Button className="button button--plain" onClick={() => { setEditingUser(null); setError(""); }}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr key={u.uuid}>
                      <td style={CLOUD_TABLE_STYLES.td}>
                        <div>{u.first_name} {u.last_name}</div>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>{u.email}</div>
                      </td>
                      <td style={CLOUD_TABLE_STYLES.td}>{u.username || "—"}</td>
                      <td style={{ ...CLOUD_TABLE_STYLES.td, textTransform: "capitalize" }}>{u.user_type}</td>
                      <td style={CLOUD_TABLE_STYLES.td}>
                        <span style={{ ...CLOUD_STATUS_PILL, ...(CLOUD_STATUS_COLORS[u.status] || CLOUD_STATUS_COLORS.inactive) }}>
                          {u.status}
                        </span>
                      </td>
                      <td style={{ ...CLOUD_TABLE_STYLES.td, textAlign: "center" }}>
                        {renderBoolIcon(u.password_set, "Password set", "No password on file")}
                      </td>
                      <td style={{ ...CLOUD_TABLE_STYLES.td, textAlign: "center" }}>
                        {renderBoolIcon(u.is_sso, "Uses SSO", "Does not use SSO")}
                      </td>
                      <td style={mutedCell}>{formatCloudUserDate(u.credentials_sent_at)}</td>
                      <td style={mutedCell}>{formatCloudUserDate(u.created_at)}</td>
                      <td style={mutedCell}>{formatCloudUserDate(u.updated_at)}</td>
                      <td style={{ ...CLOUD_TABLE_STYLES.td, textAlign: "right" }}>
                        <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                          <div style={{ display: "inline-flex", gap: 8 }}>
                            <button type="button" title="Send Login Credentials" style={CLOUD_ICON_BTN}
                              disabled={u.status !== "active" || sendingUuid === u.uuid}
                              onClick={() => setConfirmSend(u)}>
                              <PaperAirplaneIcon style={{ width: 16, height: 16, color: disabledColor(u.status !== "active") }} />
                            </button>
                            <button type="button" title={u.is_sso ? "SSO users don't have a password" : "Set Password"} style={CLOUD_ICON_BTN}
                              disabled={u.is_sso}
                              onClick={() => { setPasswordUser(u); setPasswordValue(""); setPasswordError(""); }}>
                              <KeyIcon style={{ width: 16, height: 16, color: disabledColor(u.is_sso) }} />
                            </button>
                            {cloudType === "clddeltek" && (
                              <button type="button" style={CLOUD_ICON_BTN}
                                title={!u.username ? "No username on file" : u.is_sso ? "SSO users don't have a password" : "Reset AD Password"}
                                disabled={!u.username || u.is_sso || resettingUuid === u.uuid}
                                onClick={() => setConfirmReset(u)}>
                                <LockClosedIcon style={{ width: 16, height: 16, color: disabledColor(!u.username || u.is_sso) }} />
                              </button>
                            )}
                            <button type="button" title="Edit" style={CLOUD_ICON_BTN}
                              onClick={() => { setEditingUser({ ...u }); setError(""); setShowForm(false); }}>
                              <PencilIcon style={{ width: 16, height: 16, color: "#374151" }} />
                            </button>
                            <button type="button" title="Delete" style={CLOUD_ICON_BTN}
                              onClick={() => setConfirmDelete(u)}>
                              <TrashIcon style={{ width: 16, height: 16, color: "#dc2626" }} />
                            </button>
                          </div>
                          {sendingUuid === u.uuid && (
                            <span style={{ fontSize: 11, color: "#6b7280" }}>Sending...</span>
                          )}
                          {resettingUuid === u.uuid && (
                            <span style={{ fontSize: 11, color: "#6b7280" }}>Resetting password...</span>
                          )}
                          {sendResult[u.uuid] && sendingUuid !== u.uuid && resettingUuid !== u.uuid && (
                            <span style={{ fontSize: 11, color: sendResult[u.uuid].ok ? "#38a169" : "#e53e3e" }}>
                              {sendResult[u.uuid].message}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          )}
          {!loading && cloudUsers.length === 0 && !showForm && (
            <p style={{ color: "#718096", marginBottom: "12px" }}>No cloud users on file.</p>
          )}
          {showForm && (
            <div style={{ ...formBox, marginTop: "8px" }}>
              <h3 style={{ marginTop: 0 }}>New Cloud User</h3>
              <CloudUserFields value={newUser} onChange={setNewUser} />
              {error && <p style={{ color: "#e53e3e" }}>{error}</p>}
              <div className="button-group" style={{ marginTop: "12px" }}>
                <Button className="button" onClick={saveNew} disabled={saving || !isComplete(newUser)}>
                  {saving ? "Saving..." : "Add"}
                </Button>
                <Button className="button button--plain" onClick={() => { setShowForm(false); setNewUser(EMPTY_CLOUD_USER); setError(""); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: "20px", paddingTop: "16px", borderTop: "1px solid #f3f4f6" }}>
            {!editingUser && !showForm && (
              <Button className="button button--plain" onClick={() => { setShowForm(true); setError(""); }} disabled={atLimit}>
                + Add Cloud User
              </Button>
            )}
            <Button className="button button--plain" onClick={close}>Close</Button>
          </div>
        </div>
      </Modal>

      <Modal show={!!passwordUser}>
        <div style={{ padding: "24px", width: "100%" }}>
          <h2 style={{ margin: "0 0 16px 0" }}>Set Cloud User Password</h2>
          <p style={{ marginBottom: "20px" }}>
            Set a new password for {passwordUser ? userLabel(passwordUser) : "this cloud user"}. This does not send the
            credentials email — use "Send Login Credentials" separately if needed.
          </p>
          <TextInput label="New Password" type="text" value={passwordValue}
            onChange={(e) => setPasswordValue(e.target.value)} />
          {passwordError && <p style={{ color: "#e53e3e", marginTop: "8px" }}>{passwordError}</p>}
          <div className="button-group" style={{ marginTop: "20px" }}>
            <Button className="button button--primary" disabled={passwordSaving} onClick={savePassword}>
              {passwordSaving ? "Saving..." : "Save"}
            </Button>
            <Button className="button button--plain" onClick={() => { setPasswordUser(null); setPasswordValue(""); setPasswordError(""); }}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        show={!!confirmSend}
        title="Send Login Credentials"
        confirmLabel="Send"
        onConfirm={() => { const u = confirmSend; setConfirmSend(null); sendCredentials(u.uuid); }}
        onCancel={() => setConfirmSend(null)}
      >
        Are you sure you want to send login credentials to {confirmSend ? userLabel(confirmSend) : "this cloud user"}?
      </ConfirmModal>

      <ConfirmModal
        show={!!confirmReset}
        title="Reset AD Password"
        confirmLabel="Reset Password"
        onConfirm={() => { const u = confirmReset; setConfirmReset(null); resetPassword(u.uuid); }}
        onCancel={() => setConfirmReset(null)}
      >
        Are you sure you want to force a password reset for {confirmReset ? userLabel(confirmReset, true) : "this cloud user"}?
        Their current AWS Deltek Cloud password will stop working. (Their Citrix session will also be reset.)
      </ConfirmModal>

      <ConfirmModal
        show={!!confirmDelete}
        title="Delete Cloud User"
        confirmLabel="Delete"
        confirmClass="button button--danger"
        onConfirm={() => remove(confirmDelete.uuid)}
        onCancel={() => setConfirmDelete(null)}
      >
        Are you sure you want to delete {confirmDelete ? userLabel(confirmDelete) : "this cloud user"}? This cannot be undone.
      </ConfirmModal>
    </>
  );
}
